/**
 * S-27 › Privacy › Hidden Words manager (V8 — ledger #66a).
 * ≤100 entries, case-insensitive whole-word + emoji match; matching incoming
 * messages arrive folded behind ©chat.hidden_by_filters — the sender is never
 * told. Serious surface.
 */
import { useEffect, useState } from "react";
import { copy } from "@humble/shared";
import { api, isApiError } from "../../lib/api.js";
import { Button, Card, toast } from "../../design/index.js";
import { rawApi } from "./raw-api.js";
import "./safety.css";

export function HiddenWordsScreen() {
  const [phrases, setPhrases] = useState<string[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    rawApi<{ phrases: string[] }>("GET", "/settings/hidden-words")
      .then((r) => setPhrases(r.phrases))
      .catch(() => setPhrases([]));
  }, []);

  const save = async (next: string[]) => {
    setBusy(true);
    try {
      await api.put("/settings/hidden-words", { body: { phrases: next } as never });
      setPhrases(next);
    } catch (err) {
      toast(isApiError(err) ? err.message : "Couldn't save.", "error");
    } finally {
      setBusy(false);
    }
  };

  const add = () => {
    const p = draft.trim().slice(0, 60);
    if (!p) return;
    if (phrases.length >= 100) {
      toast("List is full (100 entries).", "error");
      return;
    }
    if (phrases.some((x) => x.toLowerCase() === p.toLowerCase())) {
      setDraft("");
      return;
    }
    setDraft("");
    void save([...phrases, p]);
  };

  return (
    <div className="safety-page" style={{ maxWidth: 560 }}>
      <h1>Hidden Words</h1>
      <p className="text-dim">{copy.privacy.hidden_words}</p>
      <Card padding="md">
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="text"
            value={draft}
            maxLength={60}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") add();
            }}
            placeholder="Add a word or phrase"
            style={{ flex: 1 }}
            aria-label="Add a hidden word or phrase"
          />
          <Button variant="primary" onClick={add} loading={busy} disabled={!draft.trim()}>
            Add
          </Button>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
          {phrases.length === 0 && (
            <span className="text-faint">No entries yet — messages arrive unfiltered.</span>
          )}
          {phrases.map((p) => (
            <span key={p} className="safety-phrase">
              {p}
              <button
                type="button"
                aria-label={`Remove ${p}`}
                onClick={() => void save(phrases.filter((x) => x !== p))}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <p className="text-faint" style={{ fontSize: "0.8rem", marginBottom: 0 }}>
          {phrases.length}/100 entries
        </p>
      </Card>
    </div>
  );
}
